import type { FunctionComponent } from 'react'
import {
  type GenericAiWidgetData,
  type TextToSpeechWidgetData,
  type UpdateWithAiWidgetData,
  type CodeSnippetWidgetData,
  type SetTagWidgetData,
  type RecommendPlaybooksWidgetData,
  type AiTeammateWidgetData,
  WidgetOutputMode,
} from '@playbooks/action-cards-utils'
import { type ConnectToCrmWidgetData } from '@playbooks/core/src/interfaces/ConnectToCrmWidget'
import { Card, CardContent } from '@playbooks/ui/components/card'
import { WidgetType } from '@playbooks/core/src/interfaces/ActionCard'

import GenericAiWidget from './genericAiWidget'
import TextToSpeechWidget from './textToSpeechWidget'
import UpdateWithAiWidget from './updateWithAiWidget'
import CreateCrmObjectWidget from './createCrmObjectWidget'
import CodeSnippetWidget from './codeSnippetWidget'
import SetTagWidget from './setTagWidget'
import { type WidgetProps } from './widgetTypes'
import ConnectToCrmWidget from './connectToCrmWidget/ConnectToCrmWidget'
import RecommendPlaybooksWidget from './recommendPlaybooksWidget'
import AiTeammateWidget from './aiTeammateWidget'

/**
 * Universal widget component. Can be added to various parts of Action Cards.
 * See concrete widget components for more details.
 * @story User Story 8109: Productize AI-supported sections
 */
const Widget: FunctionComponent<WidgetProps> = ({ widget, context }) => {
  function obtainWidgetComponent() {
    switch (widget.type) {
      case WidgetType.GenericAiPrompt:
        return <GenericAiWidget widget={widget as GenericAiWidgetData} context={context} />
      case WidgetType.UpdateWithAi:
        return <UpdateWithAiWidget widget={widget as UpdateWithAiWidgetData} context={context} />
      case WidgetType.TextToSpeech:
        return <TextToSpeechWidget widget={widget as TextToSpeechWidgetData} context={context} />
      case WidgetType.CreateCrmObject:
        return <CreateCrmObjectWidget />
      case WidgetType.CodeSnippet:
        return <CodeSnippetWidget widget={widget as CodeSnippetWidgetData} />
      case WidgetType.SetTag:
        return <SetTagWidget widget={widget as SetTagWidgetData} />
      case WidgetType.ConnectToCrm:
        return <ConnectToCrmWidget widget={widget as ConnectToCrmWidgetData} />
      case WidgetType.RecommendPlaybooks:
        return <RecommendPlaybooksWidget widget={widget as RecommendPlaybooksWidgetData} />
      case WidgetType.AiTeammate:
        return <AiTeammateWidget widget={widget as AiTeammateWidgetData} context={context} />
      default:
        return null
    }
  }
  const widgetComponent = obtainWidgetComponent()

  const withoutFrame =
    widget.type === WidgetType.SetTag ||
    widget.type === WidgetType.TextToSpeech ||
    widget.type === WidgetType.UpdateWithAi ||
    (widget as GenericAiWidgetData).outputMode === WidgetOutputMode.Replace ||
    // In Teammate, the frame is only used to display the log or the transcript
    (widget.type === WidgetType.AiTeammate &&
      !((widget as AiTeammateWidgetData).showLog ?? false) &&
      !((widget as AiTeammateWidgetData).recordTranscript ?? false))

  return withoutFrame ? (
    widgetComponent
  ) : (
    <Card className="py-4 shadow-none rounded-sm bg-secondary">
      <CardContent className="px-4">{widgetComponent}</CardContent>
    </Card>
  )
}

export default Widget
